import type * as React from 'react';
import { useObjectRef } from 'react-aria';

import { useEventCallback } from '@gitbook/util-react-hooks';

import type { PaletteDef } from '../types';
import { type PaletteHandle, usePaletteHandle } from '../usePaletteHandle';

export type UsePaletteTriggerProps<T extends HTMLElement = HTMLButtonElement> = {
    palette: () => PaletteDef;
    isDisabled?: boolean;
    ref?: React.Ref<T | null>;
};

export type UsePaletteTrigger<T extends HTMLElement = HTMLButtonElement> = {
    triggerProps: {
        ref: React.MutableRefObject<T | null>;
        onPress: () => void;
        disabled: boolean | undefined;
        'aria-expanded': boolean | undefined;
    };
    open: () => void;
} & Omit<PaletteHandle, 'open'>;

/**
 * Create a palette trigger that can be used to open a palette from a button.
 */
export function usePaletteTrigger<T extends HTMLElement = HTMLButtonElement>(
    props: UsePaletteTriggerProps<T>
): UsePaletteTrigger<T> {
    const { palette, ref: propRef, isDisabled } = props;
    const ref = useObjectRef(propRef);
    const handle = usePaletteHandle(ref);
    const open = useEventCallback(() =>
        handle.open(typeof palette === 'function' ? palette() : palette)
    );
    const onPress = () => {
        if (isDisabled) {
            return null;
        }
        if (handle.opened) {
            handle.close();
        } else {
            open();
        }
    };
    return {
        ...handle,
        open,
        triggerProps: {
            ref,
            onPress,
            'aria-expanded': handle.opened || undefined,
            disabled: isDisabled,
        },
    };
}
